import { Fragment, type RefObject } from 'react';
import {
    Building2,
    ChevronRight,
    FileText,
    Loader2,
    Pencil,
    Search,
    Users,
    Zap,
} from 'lucide-react';
import type { SowPoolUsageDisplayMetrics } from '@wevo/wevo-access/sow-credit-pools';

import type {
    AdminCompanyAdminStatus,
    AdminPulseSubscription,
    AdminUserRow,
} from '../../../services/api';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AgreementStatusBadge } from './AgreementStatusBadge';
import { isImmediateSearchKey } from '../adminDashboardHelpers';
import { AddUsersViaUsersPageNote, CompanyDetailTabButton } from './CompanyDetailPrimitives';
import { CompanyUsersPaginatedSection } from './CompanyUsersPaginatedSection';
import { SowTableUsageMetrics } from './SowUsageDisplay.tsx';
import { formatCredits } from './adminCreditAdapters';
import { getPulseSubscriptionStatus } from './pulseSubscriptionStatus';
import type { CompanyDetailTab } from './companyDetailTabs';

type CompanySowRowLike = {
    id: string;
    name: string;
    startDate: string;
    endDate?: string | null;
    state: string;
    available: number;
    proUsage: SowPoolUsageDisplayMetrics | null;
    pulseUsage: SowPoolUsageDisplayMetrics | null;
};

type CompanyTeamRow = {
    id: string;
    name: string;
    isActive?: boolean;
    memberCount?: number;
    activeMemberCount?: number;
    members: Array<{
        user: {
            id: string | number;
            name: string;
            email: string;
        };
        membershipActive?: boolean;
    }>;
};

type InlineTeamEdit = {
    teamId: number;
    draft: string;
    originalName: string;
} | null;

export function CompanyDetailTabBar({
    activeTab,
    sowCount,
    pulseSubscriptionCount,
    teamCount,
    userCount,
    onSelectTab,
}: {
    activeTab: CompanyDetailTab;
    sowCount: number;
    pulseSubscriptionCount: number;
    teamCount: number;
    userCount: number;
    onSelectTab: (tab: CompanyDetailTab) => void;
}) {
    return (
        <div className="admin-tab-switcher-track">
            <CompanyDetailTabButton
                active={activeTab === 'sows'}
                onClick={() => onSelectTab('sows')}
                icon={FileText}
                label={`SOWs (${sowCount})`}
            />
            <CompanyDetailTabButton
                active={activeTab === 'teams'}
                onClick={() => onSelectTab('teams')}
                icon={Building2}
                label={`Teams (${teamCount})`}
            />
            <CompanyDetailTabButton
                active={activeTab === 'users'}
                onClick={() => onSelectTab('users')}
                icon={Users}
                label={`Users (${userCount})`}
            />
            <CompanyDetailTabButton
                active={activeTab === 'pulse-subscriptions'}
                onClick={() => onSelectTab('pulse-subscriptions')}
                icon={Zap}
                label={`Pulse Subs (${pulseSubscriptionCount})`}
            />
        </div>
    );
}

export function CompanyPulseSubscriptionsTab({
    subscriptions,
    isPending,
    isError,
    onRowClick,
}: {
    subscriptions: AdminPulseSubscription[];
    isPending: boolean;
    isError: boolean;
    onRowClick: (subscription: AdminPulseSubscription) => void;
}) {
    if (isError) {
        return (
            <Card>
                <CardContent className="p-8 text-sm text-neutral-600">
                    Could not load Pulse subscriptions.
                </CardContent>
            </Card>
        );
    }
    if (isPending) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-sm text-neutral-500">
                    Loading Pulse subscriptions…
                </CardContent>
            </Card>
        );
    }
    return (
        <Card className="overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Subscription ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Scope</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="text-right">Total credits</TableHead>
                        <TableHead className="text-right">Seats</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {subscriptions.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} className="py-8 text-center text-neutral-500">
                                No Pulse subscriptions for this company.
                            </TableCell>
                        </TableRow>
                    ) : (
                        subscriptions.map((subscription) => (
                            <TableRow
                                key={subscription.id}
                                className="cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={() => onRowClick(subscription)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onRowClick(subscription);
                                    }
                                }}
                            >
                                <TableCell className="font-mono text-xs">
                                    {subscription.id}
                                </TableCell>
                                <TableCell>
                                    <AgreementStatusBadge
                                        status={getPulseSubscriptionStatus(
                                            subscription.startedAt,
                                            subscription.expiresAt,
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    <p className="font-medium capitalize">{subscription.scope}</p>
                                    <p className="text-xs text-neutral-500">
                                        {subscription.scope === 'company'
                                            ? `Company #${subscription.companyId}`
                                            : `${subscription.teamName ?? 'Team'} #${
                                                  subscription.teamId
                                              }`}
                                        {subscription.sowId ? ` · SOW ${subscription.sowId}` : ''}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    {subscription.tierType === 'enterprise_placeholder'
                                        ? 'Enterprise'
                                        : (subscription.tierName ?? subscription.tierType ?? '—')}
                                </TableCell>
                                <TableCell>
                                    {subscription.startedAt
                                        ? new Date(subscription.startedAt).toLocaleString()
                                        : '—'}
                                </TableCell>
                                <TableCell>
                                    {subscription.expiresAt
                                        ? new Date(subscription.expiresAt).toLocaleString()
                                        : 'Open'}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {subscription.totalCredits === null
                                        ? '—'
                                        : formatCredits(subscription.totalCredits)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {subscription.numSeats}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Card>
    );
}

export function CompanySowsTab({
    rows,
    onRowClick,
}: {
    rows: CompanySowRowLike[];
    onRowClick: (sow: CompanySowRowLike) => void;
}) {
    return (
        <Card className="overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>SOW</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Pro Usage</TableHead>
                        <TableHead className="text-right">Pulse Usage</TableHead>
                        <TableHead className="text-right">Available</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((sow) => (
                        <TableRow
                            key={sow.id}
                            className="cursor-pointer"
                            onClick={() => onRowClick(sow)}
                        >
                            <TableCell>
                                <p className="font-medium text-neutral-950">{sow.name}</p>
                                <p className="text-xs text-neutral-500">{sow.id}</p>
                            </TableCell>
                            <TableCell>
                                {new Date(sow.startDate).toLocaleDateString()} -{' '}
                                {sow.endDate ? new Date(sow.endDate).toLocaleDateString() : 'Open'}
                            </TableCell>
                            <TableCell>
                                <AgreementStatusBadge status={sow.state} />
                            </TableCell>
                            <TableCell className="text-right">
                                {sow.proUsage ? (
                                    <SowTableUsageMetrics
                                        used={sow.proUsage.used}
                                        granted={sow.proUsage.granted}
                                        usageCommitted={sow.proUsage.usageCommitted}
                                        usagePending={sow.proUsage.usagePending}
                                        usagePercent={sow.proUsage.usagePercent}
                                        committedUsagePercent={sow.proUsage.committedUsagePercent}
                                        pendingUsagePercent={sow.proUsage.pendingUsagePercent}
                                    />
                                ) : (
                                    <span className="text-neutral-500">-</span>
                                )}
                            </TableCell>
                            <TableCell className="text-right">
                                {sow.pulseUsage ? (
                                    <SowTableUsageMetrics
                                        used={sow.pulseUsage.used}
                                        granted={sow.pulseUsage.granted}
                                        usageCommitted={sow.pulseUsage.usageCommitted}
                                        usagePending={sow.pulseUsage.usagePending}
                                        usagePercent={sow.pulseUsage.usagePercent}
                                        committedUsagePercent={sow.pulseUsage.committedUsagePercent}
                                        pendingUsagePercent={sow.pulseUsage.pendingUsagePercent}
                                    />
                                ) : (
                                    <span className="text-neutral-500">-</span>
                                )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-medium text-neutral-950">
                                {formatCredits(sow.available)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

export function CompanyTeamsTab({
    teams,
    expandedTeamIds,
    inlineTeamEdit,
    inlineTeamNameInputRef,
    onToggleExpanded,
    onStartInlineEdit,
    onInlineTeamDraftChange,
    onFinishInlineTeamNameEdit,
    onSetTeamActive,
    onSetMembershipActive,
    teamActivePendingTeamId,
    teamMembershipLocked,
    teamMembershipPending,
}: {
    teams: CompanyTeamRow[];
    expandedTeamIds: Set<string>;
    inlineTeamEdit: InlineTeamEdit;
    inlineTeamNameInputRef: RefObject<HTMLInputElement>;
    onToggleExpanded: (teamId: string) => void;
    onStartInlineEdit: (teamId: number, name: string) => void;
    onInlineTeamDraftChange: (teamId: number, value: string) => void;
    onFinishInlineTeamNameEdit: () => Promise<void>;
    onSetTeamActive: (teamId: number, isActive: boolean) => void;
    onSetMembershipActive: (teamId: number, userId: number, isActive: boolean) => void;
    teamActivePendingTeamId: number | null;
    teamMembershipLocked: boolean;
    teamMembershipPending: { teamId: number; userId: number } | null;
}) {
    return (
        <>
            <AddUsersViaUsersPageNote />
            <Card className="overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10" />
                            <TableHead>Team</TableHead>
                            <TableHead className="text-right">Active members</TableHead>
                            <TableHead className="w-28 text-right">Active</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {teams.map((team) => {
                            const teamNumericId = Number(team.id);
                            const teamIsActive = team.isActive !== false;
                            const expanded = expandedTeamIds.has(team.id);
                            const activeMembers = team.activeMemberCount ?? team.memberCount ?? 0;
                            return (
                                <Fragment key={team.id}>
                                    <TableRow className="align-middle">
                                        <TableCell className="w-10">
                                            <button
                                                type="button"
                                                className="flex size-8 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100"
                                                aria-expanded={expanded}
                                                aria-label={
                                                    expanded
                                                        ? 'Hide team members'
                                                        : 'Show team members'
                                                }
                                                onClick={() => onToggleExpanded(team.id)}
                                            >
                                                <ChevronRight
                                                    className={`size-4 transition-transform ${
                                                        expanded ? 'rotate-90' : ''
                                                    }`}
                                                />
                                            </button>
                                        </TableCell>
                                        <TableCell
                                            className={
                                                teamIsActive
                                                    ? 'text-neutral-950'
                                                    : 'text-neutral-500'
                                            }
                                        >
                                            {inlineTeamEdit?.teamId === teamNumericId ? (
                                                <Input
                                                    ref={inlineTeamNameInputRef}
                                                    className="inline-flex h-8 max-w-[min(100%,20rem)] font-medium"
                                                    value={inlineTeamEdit.draft}
                                                    onChange={(event) =>
                                                        onInlineTeamDraftChange(
                                                            teamNumericId,
                                                            event.target.value,
                                                        )
                                                    }
                                                    onBlur={() => {
                                                        void onFinishInlineTeamNameEdit();
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            (
                                                                event.currentTarget as HTMLInputElement
                                                            ).blur();
                                                        }
                                                    }}
                                                    autoComplete="off"
                                                    aria-label={`Edit ${team.name} team name`}
                                                    disabled={
                                                        teamActivePendingTeamId === teamNumericId
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    <span className="font-medium">{team.name}</span>
                                                    <button
                                                        type="button"
                                                        className="ml-2 inline-flex size-7 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                                                        aria-label={`Edit ${team.name} team name`}
                                                        disabled={
                                                            !Number.isFinite(teamNumericId) ||
                                                            teamActivePendingTeamId ===
                                                                teamNumericId
                                                        }
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            if (!Number.isFinite(teamNumericId)) {
                                                                return;
                                                            }
                                                            onStartInlineEdit(
                                                                teamNumericId,
                                                                team.name,
                                                            );
                                                        }}
                                                    >
                                                        <Pencil className="size-3.5" />
                                                    </button>
                                                </>
                                            )}
                                            <span className="ml-1.5 font-normal text-neutral-500">
                                                (#{team.id})
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {activeMembers}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <input
                                                type="checkbox"
                                                className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                                                checked={teamIsActive}
                                                disabled={
                                                    !Number.isFinite(teamNumericId) ||
                                                    teamActivePendingTeamId === teamNumericId
                                                }
                                                onChange={(event) => {
                                                    if (!Number.isFinite(teamNumericId)) {
                                                        return;
                                                    }
                                                    onSetTeamActive(
                                                        teamNumericId,
                                                        event.target.checked,
                                                    );
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    {expanded ? (
                                        <TableRow
                                            key={`${team.id}-members`}
                                            className="bg-neutral-50 hover:bg-neutral-50"
                                        >
                                            <TableCell colSpan={4} className="p-0">
                                                <div className="px-4 py-3 pl-12">
                                                    {team.members.length === 0 ? (
                                                        <p className="text-sm text-neutral-500">
                                                            No member assignments for this team yet.
                                                        </p>
                                                    ) : (
                                                        <ul className="divide-y divide-neutral-200/80">
                                                            {team.members.map((member) => {
                                                                const userNumericId = Number(
                                                                    member.user.id,
                                                                );
                                                                const membershipOn =
                                                                    member.membershipActive !==
                                                                    false;
                                                                const membershipBusy =
                                                                    teamMembershipPending?.teamId ===
                                                                        teamNumericId &&
                                                                    teamMembershipPending?.userId ===
                                                                        userNumericId;
                                                                return (
                                                                    <li
                                                                        key={member.user.id}
                                                                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 py-2.5 first:pt-0 last:pb-0"
                                                                    >
                                                                        <div className="min-w-0">
                                                                            <p className="font-medium text-neutral-950">
                                                                                {member.user.name}
                                                                            </p>
                                                                            <p className="mt-0.5 text-sm text-neutral-500">
                                                                                {member.user.email}
                                                                                <span className="ml-1.5 text-neutral-400">
                                                                                    (#
                                                                                    {member.user.id}
                                                                                    )
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-neutral-700">
                                                                            <span>
                                                                                Active Membership
                                                                            </span>
                                                                            <input
                                                                                type="checkbox"
                                                                                className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                                                                                checked={
                                                                                    membershipOn
                                                                                }
                                                                                disabled={
                                                                                    !Number.isFinite(
                                                                                        teamNumericId,
                                                                                    ) ||
                                                                                    !Number.isFinite(
                                                                                        userNumericId,
                                                                                    ) ||
                                                                                    teamMembershipLocked
                                                                                }
                                                                                onChange={(
                                                                                    event,
                                                                                ) => {
                                                                                    if (
                                                                                        !Number.isFinite(
                                                                                            teamNumericId,
                                                                                        ) ||
                                                                                        !Number.isFinite(
                                                                                            userNumericId,
                                                                                        )
                                                                                    ) {
                                                                                        return;
                                                                                    }
                                                                                    onSetMembershipActive(
                                                                                        teamNumericId,
                                                                                        userNumericId,
                                                                                        event.target
                                                                                            .checked,
                                                                                    );
                                                                                }}
                                                                            />
                                                                            {membershipBusy ? (
                                                                                <span
                                                                                    role="status"
                                                                                    aria-label="Updating membership"
                                                                                    className="inline-flex items-center text-neutral-500"
                                                                                >
                                                                                    <Loader2
                                                                                        className="size-3 animate-spin"
                                                                                        aria-hidden
                                                                                    />
                                                                                </span>
                                                                            ) : null}
                                                                        </label>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Card>
        </>
    );
}

export function CompanyUsersTab({
    isError,
    isPending,
    data,
    page,
    pageSize,
    searchDraft,
    onSearchChange,
    onSearchCommit,
    onPageChange,
    onRowClick,
    onSync,
    syncingUserId,
    companyAdminStatusByUserId,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
    activeCompanyMemberUserIds,
    companyAdminPendingUserId,
    onSetCompanyUserAdmin,
}: {
    isError: boolean;
    isPending: boolean;
    data:
        | {
              items: AdminUserRow[];
              total: number;
              totalPages: number;
          }
        | undefined;
    page: number;
    pageSize: number;
    searchDraft: string;
    onSearchChange: (value: string) => void;
    onSearchCommit: (value?: string) => void;
    onPageChange: (page: number) => void;
    onRowClick: (user: AdminUserRow) => void;
    onSync: (userId: number) => void;
    syncingUserId: number | null;
    companyAdminStatusByUserId: Map<number, AdminCompanyAdminStatus>;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
    activeCompanyMemberUserIds: Set<number>;
    companyAdminPendingUserId: number | null;
    onSetCompanyUserAdmin: (userId: number, isAdmin: boolean) => void;
}) {
    return (
        <>
            <div className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                    <Input
                        value={searchDraft}
                        onChange={(event) => onSearchChange(event.target.value)}
                        onKeyDown={(event) => {
                            if (isImmediateSearchKey(event.key)) {
                                onSearchCommit(event.currentTarget.value);
                            }
                        }}
                        placeholder="Search by user ID, name, or email"
                        aria-label="Search company users"
                        className="border-0 admin-field-fill pl-10 shadow-none focus-visible:ring-0"
                    />
                </div>
            </div>
            <AddUsersViaUsersPageNote />
            {isError && (
                <Card>
                    <CardContent className="p-8 text-sm text-neutral-600">
                        Could not load company users (check administrate:users permission).
                    </CardContent>
                </Card>
            )}
            {!isError && isPending && !data && (
                <Card>
                    <CardContent className="p-8 text-center text-sm text-neutral-500">
                        Loading users…
                    </CardContent>
                </Card>
            )}
            {!isError && data && (
                <CompanyUsersPaginatedSection
                    rows={data.items}
                    page={page}
                    pageSize={pageSize}
                    total={data.total}
                    totalPages={data.totalPages}
                    onPageChange={onPageChange}
                    onRowClick={onRowClick}
                    onSync={onSync}
                    syncingUserId={syncingUserId}
                    companyAdminStatusByUserId={companyAdminStatusByUserId}
                    isCompanyAdminStatusPending={isCompanyAdminStatusPending}
                    isCompanyAdminStatusError={isCompanyAdminStatusError}
                    activeCompanyMemberUserIds={activeCompanyMemberUserIds}
                    companyAdminPendingUserId={companyAdminPendingUserId}
                    onSetCompanyUserAdmin={onSetCompanyUserAdmin}
                />
            )}
        </>
    );
}
